// Since you last looked (K7, B-411, `GET`/`PUT /api/leagues/:id/catch-up`):
// what happened in a league after its front page was last shown to me.
//
// READ ONCE A VISIT, MARKED ONCE SHOWN. The front page reads the catch-up the
// first time it is drawn in a visit, and says it has shown it (the `PUT`)
// only once it is on screen — a page left before the read landed marks
// nothing. What was shown is kept for the rest of the visit, so going back to
// League shows the same catch-up until **Got it** puts it away, rather than
// an empty one counted from the mark just made.
//
// A VISIT ENDS WITH THE PAGE AWAY FOR HALF AN HOUR as well as with a reload:
// an installed app resumed from the background, or a tab left open over a
// weekend, is a new visit and reads again (the review of B-411). The shell
// times the absence (`shell/visits.ts`), since this layer never reads the
// machine's clock, and says so with `endCatchUpVisit`.
//
// NOTHING ON A FAILED READ, and no mark: the next visit asks again, from the
// same look.

import { CatchUpLooked, CatchUpView } from '@fsp/domain';
import { useEffect, useState } from 'react';
import { get, send } from './api';

const path = (leagueId: string) => `/leagues/${encodeURIComponent(leagueId)}/catch-up`;

/** This visit's catch-ups, by account and league; null once put away. */
const visit = new Map<string, CatchUpView | null>();
/** The catch-ups this visit has marked as shown. */
const marked = new Set<string>();
/** A read on its way, so a screen drawn twice asks once. */
const asking = new Map<string, Promise<void>>();
/** Bumped when a visit ends, so a page on screen reads again. */
let visitNumber = 0;
const listeners = new Set<() => void>();

/** The visit is over: what was read and marked goes, and a page on screen reads again. */
export function endCatchUpVisit(): void {
  visit.clear();
  marked.clear();
  visitNumber += 1;
  for (const listener of listeners) listener();
}

/** Read it and keep it for the visit; nothing kept on a failed read. */
function ask(key: string, leagueId: string): Promise<void> {
  const already = asking.get(key);
  if (already !== undefined) return already;
  const read = get(path(leagueId), CatchUpView).then((reply) => {
    asking.delete(key);
    if (reply.status !== 'ok' || visit.has(key)) return;
    visit.set(key, reply.data);
  });
  asking.set(key, read);
  return read;
}

export function useCatchUp(
  userId: string | null,
  leagueId: string | null,
): { view: CatchUpView | null; done: () => void } {
  const key = userId === null || leagueId === null ? null : `${userId}|${leagueId}`;
  const [shown, setShown] = useState<{ key: string; view: CatchUpView } | null>(null);
  const [visitSeen, setVisitSeen] = useState(visitNumber);
  useEffect(() => {
    const listener = () => setVisitSeen(visitNumber);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  useEffect(() => {
    // ANOTHER LEAGUE'S CATCH-UP IS NEVER THIS ONE'S: what is on screen goes
    // with the league it was read for.
    if (key === null || leagueId === null) {
      setShown(null);
      return;
    }
    const kept = visit.get(key);
    setShown(kept ? { key, view: kept } : null);
    if (visit.has(key)) return;
    let live = true;
    void ask(key, leagueId).then(() => {
      const view = visit.get(key);
      if (live && view) setShown({ key, view });
    });
    return () => {
      live = false;
    };
  }, [key, visitSeen]);
  // SHOWN, SO LOOKED: marked once it is on screen, the next visit counting
  // from now. Once a visit, whatever the page redraws.
  const view = shown !== null && shown.key === key ? shown.view : null;
  useEffect(() => {
    if (view === null || key === null || leagueId === null || marked.has(key)) return;
    marked.add(key);
    void send('PUT', path(leagueId), undefined, CatchUpLooked);
  }, [view, key]);
  return {
    view,
    done: () => {
      if (key !== null) visit.set(key, null);
      setShown(null);
    },
  };
}

/** Whether a catch-up has anything to say. */
export function hasNews(v: CatchUpView): boolean {
  return v.results.length > 0 || v.otherGames > 0 || v.moveCount > 0 || v.newsCount > 0;
}
